// ============================================
// STRATEGY ENGINE - Comprehensive Options Strategy Selection
// Based on IgniteAI 5-Step Methodology: Catalyst + Structure + Risk
// ============================================

import {
  MarketData, TechnicalData, OptionsData, RiskFlags,
  StrategyRecommendation, OptionLeg, VolatilityMetrics,
  TrailingStopConfig, TieredExitConfig, MomentumOverride
} from '../types';

// Round strike price to nearest tradeable increment (Robinhood/exchange compatible)
// Standard option chains: $1 increments for most stocks, $5 for high-priced stocks
function roundStrike(price: number): number {
  if (price <= 0) return 1;
  if (price < 5) return Math.round(price * 2) / 2;  // $0.50 increments under $5
  if (price < 200) return Math.round(price);          // $1 increments under $200
  return Math.round(price / 5) * 5;                   // $5 increments $200+
}

// Strategy types available
export type StrategyType =
  | 'LONG_CALL'
  | 'LONG_PUT'
  | 'BULL_CALL_SPREAD'
  | 'BEAR_PUT_SPREAD'
  | 'BULL_PUT_SPREAD'  // Credit spread
  | 'BEAR_CALL_SPREAD' // Credit spread
  | 'IRON_CONDOR'
  | 'IRON_BUTTERFLY'
  | 'STRADDLE'
  | 'STRANGLE'
  | 'CALENDAR_SPREAD'
  | 'DIAGONAL_SPREAD'
  | 'COVERED_CALL'
  | 'CASH_SECURED_PUT';

export type MarketDirection = 'BULLISH' | 'BEARISH' | 'NEUTRAL';
export type IVEnvironment = 'LOW' | 'MODERATE' | 'HIGH' | 'EXTREME';
export type ExperienceLevel = 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';

// Strategy matrix based on direction and IV
// Small account methodology: NO iron condors, straddles, strangles, naked options under $50K
// Approved: Bull Call Spread, Long Call, Call Diagonal, Bull Put Spread (rare, high IV only)
// Bearish allowed for conviction plays: Bear Put Spread, Long Put
const STRATEGY_MATRIX: Record<MarketDirection, Record<IVEnvironment, StrategyType[]>> = {
  BULLISH: {
    LOW: ['BULL_CALL_SPREAD', 'LONG_CALL', 'DIAGONAL_SPREAD'],
    MODERATE: ['BULL_CALL_SPREAD', 'LONG_CALL', 'DIAGONAL_SPREAD'],
    HIGH: ['BULL_CALL_SPREAD', 'BULL_PUT_SPREAD', 'DIAGONAL_SPREAD'],
    EXTREME: ['BULL_CALL_SPREAD', 'BULL_PUT_SPREAD']
  },
  BEARISH: {
    LOW: ['BEAR_PUT_SPREAD', 'LONG_PUT'],
    MODERATE: ['BEAR_PUT_SPREAD', 'LONG_PUT', 'BEAR_CALL_SPREAD'],
    HIGH: ['BEAR_CALL_SPREAD', 'BEAR_PUT_SPREAD'],
    EXTREME: ['BEAR_CALL_SPREAD', 'BEAR_PUT_SPREAD']
  },
  NEUTRAL: {
    LOW: ['DIAGONAL_SPREAD', 'CALENDAR_SPREAD'],
    MODERATE: ['DIAGONAL_SPREAD', 'CALENDAR_SPREAD'],
    HIGH: ['BULL_PUT_SPREAD', 'BEAR_CALL_SPREAD'],   // Defined-risk credit spreads only
    EXTREME: ['BULL_PUT_SPREAD', 'BEAR_CALL_SPREAD']
  }
};

// Expiry recommendations based on strategy and conditions
interface ExpiryRecommendation {
  dte: number;
  label: string;
  reasoning: string;
}

function getExpiryRecommendation(
  strategyType: StrategyType,
  earningsDays: number | null,
  ivRank: number,
  direction: MarketDirection
): ExpiryRecommendation {
  // Credit spreads should NEVER expire into imminent earnings
  // They benefit from IV crush AFTER earnings, not from the binary gap risk before
  const isCreditStrategy = ['BULL_PUT_SPREAD', 'BEAR_CALL_SPREAD', 'IRON_CONDOR', 'IRON_BUTTERFLY', 'CASH_SECURED_PUT'].includes(strategyType);

  // RULE: Close all positions 2 trading days BEFORE earnings. Don't enter within 2 days.
  if (earningsDays !== null && earningsDays <= 2) {
    // Too close to earnings — NO NEW ENTRIES for any strategy type
    return {
      dte: earningsDays + 30,
      label: `${earningsDays + 30} DTE (Post-Earnings)`,
      reasoning: `BLOCKED: Earnings in ${earningsDays} days. Methodology rule: close all positions 2 days before earnings. No new entries this close to a binary event. Wait for post-earnings setup.`
    };
  }

  if (earningsDays !== null && earningsDays <= 7) {
    if (isCreditStrategy) {
      // Push credit spread expiry to AFTER earnings — benefit from IV crush
      const postEarningsDte = earningsDays + 30;
      return {
        dte: postEarningsDte,
        label: `${postEarningsDte} DTE (Post-Earnings)`,
        reasoning: `Credit spread expires after earnings (day ${earningsDays}) to benefit from IV crush. Selling premium pre-earnings with pre-earnings expiry carries extreme gamma and gap risk.`
      };
    }
    // Debit strategies: expire BEFORE earnings (close 2 days prior)
    const safeExitDte = Math.max(earningsDays - 2, 1);
    return {
      dte: safeExitDte,
      label: `${safeExitDte} DTE (Pre-Earnings Exit)`,
      reasoning: `Expiring/closing before earnings on day ${earningsDays}. Rule: exit 2 trading days before binary event to avoid gap risk.`
    };
  }

  // Earnings in 2-8 weeks = CATALYST WINDOW (positive for the methodology)
  if (earningsDays !== null && earningsDays >= 14 && earningsDays <= 56) {
    // This is the sweet spot — catalyst identified, time to structure a trade
    const idealDte = Math.min(earningsDays - 3, 45); // Expire before earnings with buffer
    if (!isCreditStrategy) {
      return {
        dte: Math.max(idealDte, 30),
        label: `${Math.max(idealDte, 30)} DTE (Catalyst Window)`,
        reasoning: `Earnings catalyst in ${earningsDays} days. Positioning 30-45 DTE to capture pre-earnings run-up while expiring before the binary event.`
      };
    }
  }

  // Strategy-specific recommendations
  switch (strategyType) {
    case 'LONG_CALL':
    case 'LONG_PUT':
      // Single leg options: 45-60 DTE to balance theta decay
      if (ivRank < 30) {
        return {
          dte: 45,
          label: '45 DTE',
          reasoning: 'With low IV, longer expiration gives time for the move to develop while limiting theta decay. 45 DTE is the sweet spot for single-leg directional plays.'
        };
      } else {
        return {
          dte: 30,
          label: '30 DTE',
          reasoning: 'With elevated IV, shorter expiration reduces exposure to IV crush. 30 DTE balances theta decay with premium cost.'
        };
      }

    case 'BULL_CALL_SPREAD':
    case 'BEAR_PUT_SPREAD':
      // Debit spreads: 30-45 DTE
      return {
        dte: 35,
        label: '35 DTE',
        reasoning: 'Debit spreads perform best at 30-45 DTE. This gives enough time for the directional move while the short leg offsets theta decay.'
      };

    case 'BULL_PUT_SPREAD':
    case 'BEAR_CALL_SPREAD':
      // Credit spreads: 30-45 DTE for optimal theta decay
      return {
        dte: 45,
        label: '45 DTE',
        reasoning: 'Credit spreads benefit from theta decay. 45 DTE provides optimal balance of premium collection and probability of profit. Plan to close at 50% profit or 21 DTE.'
      };

    case 'IRON_CONDOR':
    case 'IRON_BUTTERFLY':
      // Iron strategies: 45 DTE for premium collection
      return {
        dte: 45,
        label: '45 DTE',
        reasoning: 'Iron condors perform best when opened at 45 DTE. This allows sufficient time for theta decay while maintaining manageable gamma risk. Target 50% profit or close at 21 DTE.'
      };

    case 'STRADDLE':
    case 'STRANGLE':
      // Volatility plays: depends on whether buying or selling
      if (ivRank < 30) {
        return {
          dte: 30,
          label: '30 DTE',
          reasoning: 'Long straddles/strangles need a big move. 30 DTE limits theta decay while giving time for the anticipated volatility expansion.'
        };
      } else {
        return {
          dte: 45,
          label: '45 DTE',
          reasoning: 'With high IV, selling strangles at 45 DTE maximizes theta decay benefit. The elevated premium provides cushion for the position.'
        };
      }

    case 'CALENDAR_SPREAD':
    case 'DIAGONAL_SPREAD':
      return {
        dte: 60,
        label: '60/30 DTE',
        reasoning: 'Calendar spreads use a near-term short leg (30 DTE) and a longer-term long leg (60 DTE). This setup profits from accelerated theta decay on the short leg.'
      };

    case 'COVERED_CALL':
    case 'CASH_SECURED_PUT':
      return {
        dte: 30,
        label: '30 DTE',
        reasoning: '30 DTE is optimal for covered calls and cash-secured puts. It balances premium collection with the ability to roll or adjust monthly.'
      };

    default:
      return {
        dte: 30,
        label: '30 DTE',
        reasoning: 'Standard 30-day expiration provides balanced theta decay and time for price movement.'
      };
  }
}

// Determine market direction from technicals
export function determineMarketDirection(
  tech: TechnicalData,
  market: MarketData
): { direction: MarketDirection; confidence: number; reasoning: string } {
  let bullishSignals = 0;
  let bearishSignals = 0;
  const reasons: string[] = [];

  // RSI analysis
  if (tech.rsi > 60) {
    bullishSignals += 2;
    reasons.push(`RSI ${tech.rsi.toFixed(0)} shows bullish momentum`);
  } else if (tech.rsi < 40) {
    bearishSignals += 2;
    reasons.push(`RSI ${tech.rsi.toFixed(0)} shows bearish momentum`);
  } else if (tech.rsi > 50) {
    bullishSignals += 1;
  } else {
    bearishSignals += 1;
  }

  // MACD analysis
  if (tech.macd.crossover === 'BULLISH') {
    bullishSignals += 2;
    reasons.push('MACD bullish crossover detected');
  } else if (tech.macd.crossover === 'BEARISH') {
    bearishSignals += 2;
    reasons.push('MACD bearish crossover detected');
  }

  // EMA analysis
  if (tech.priceAboveEma21) {
    bullishSignals += 1;
    if (tech.daysAboveEma21 > 10) {
      bullishSignals += 1;
      reasons.push(`Price above 21 EMA for ${tech.daysAboveEma21} days - established uptrend`);
    }
  } else {
    bearishSignals += 1;
    reasons.push('Price below 21 EMA - potential downtrend');
  }

  // Price momentum
  if (market.changePercent > 1) {
    bullishSignals += 1;
  } else if (market.changePercent < -1) {
    bearishSignals += 1;
  }

  // 52-week position analysis
  const high52 = market.high52 ?? market.price * 1.2;
  const low52 = market.low52 ?? market.price * 0.8;
  const range = high52 - low52;
  const position = range > 0 ? (market.price - low52) / range : 0.5;

  if (position > 0.8) {
    // Near 52-week high - could be strength or exhaustion
    if (tech.rsi > 70) {
      bearishSignals += 1;
      reasons.push('Near 52-week high with overbought RSI - potential reversal');
    } else {
      bullishSignals += 1;
      reasons.push('Near 52-week high with momentum - trend continuation');
    }
  } else if (position < 0.2) {
    // Near 52-week low - could be weakness or bounce
    if (tech.rsi < 30) {
      bullishSignals += 1;
      reasons.push('Near 52-week low with oversold RSI - potential bounce');
    } else {
      bearishSignals += 1;
      reasons.push('Near 52-week low with continued weakness');
    }
  }

  // Calculate final direction and confidence
  const totalSignals = bullishSignals + bearishSignals;
  const netSignals = bullishSignals - bearishSignals;

  let direction: MarketDirection;
  let confidence: number;

  if (Math.abs(netSignals) <= 1) {
    direction = 'NEUTRAL';
    confidence = 50 + Math.abs(netSignals) * 10;
  } else if (netSignals > 0) {
    direction = 'BULLISH';
    confidence = Math.min(90, 60 + netSignals * 10);
  } else {
    direction = 'BEARISH';
    confidence = Math.min(90, 60 + Math.abs(netSignals) * 10);
  }

  return {
    direction,
    confidence,
    reasoning: reasons.slice(0, 3).join('. ') + '.'
  };
}

// Determine IV environment
export function determineIVEnvironment(ivRank: number): IVEnvironment {
  if (ivRank < 25) return 'LOW';
  if (ivRank < 50) return 'MODERATE';
  if (ivRank < 75) return 'HIGH';
  return 'EXTREME';
}

// Get strategy explanation
function getStrategyExplanation(strategyType: StrategyType, direction: MarketDirection, ivEnv: IVEnvironment): string {
  const explanations: Record<StrategyType, string> = {
    'LONG_CALL': `Buying a call option is the simplest bullish strategy. With ${ivEnv.toLowerCase()} IV, premium cost is ${ivEnv === 'LOW' ? 'favorable' : 'elevated'}. Max loss is limited to premium paid.`,
    'LONG_PUT': `Buying a put option profits from downside movement. With ${ivEnv.toLowerCase()} IV, this is ${ivEnv === 'LOW' ? 'cost-effective' : 'expensive but may be worth it for protection'}.`,
    'BULL_CALL_SPREAD': 'A debit spread that profits from upward movement with defined risk. The short call reduces cost but caps profit potential. Ideal for moderately bullish outlook.',
    'BEAR_PUT_SPREAD': 'A debit spread profiting from downward movement with defined risk. The short put reduces cost but caps profit. Ideal for moderately bearish outlook.',
    'BULL_PUT_SPREAD': 'A credit spread that collects premium if price stays above the short strike. Benefits from theta decay. Ideal when bullish with high IV.',
    'BEAR_CALL_SPREAD': 'A credit spread that collects premium if price stays below the short strike. Benefits from theta decay. Ideal when bearish with high IV.',
    'IRON_CONDOR': 'A neutral strategy that profits if price stays within a range. Collects premium from both sides. Best in high IV environments with expected consolidation.',
    'IRON_BUTTERFLY': 'Similar to iron condor but with tighter strikes at-the-money. Higher premium but requires price to stay very close to current level.',
    'STRADDLE': 'Profits from big moves in either direction. Long straddle buys both call and put ATM. Best when expecting volatility expansion.',
    'STRANGLE': 'Similar to straddle but with OTM options, reducing cost. Needs larger move to profit but has lower breakeven.',
    'CALENDAR_SPREAD': 'Profits from time decay differential. Sell near-term, buy longer-term at same strike. Best in low IV expecting consolidation.',
    'DIAGONAL_SPREAD': 'Combines directional bias with time spread. More complex but offers flexibility in strike and expiry selection.',
    'COVERED_CALL': 'Sell calls against stock you own to generate income. Caps upside but reduces cost basis. Best for neutral to slightly bullish outlook.',
    'CASH_SECURED_PUT': 'Sell puts with cash to cover assignment. Collects premium while waiting to buy stock at lower price. Best when bullish with high IV.'
  };

  return explanations[strategyType] || 'Strategy selected based on current market conditions.';
}

// Define which strategies are appropriate for different risk levels
// IgniteAI methodology: defined-risk only, no naked options, no iron condors/straddles under $50K
const CONSERVATIVE_STRATEGIES: StrategyType[] = [
  'BULL_CALL_SPREAD',   // Defined risk debit - primary structure
  'BEAR_PUT_SPREAD',    // Defined risk debit - bearish conviction
  'DIAGONAL_SPREAD',    // Defined risk with theta help
  'CALENDAR_SPREAD',    // Defined risk time spread
  'BULL_PUT_SPREAD',    // Credit spread - rare, high IV near support only
  'BEAR_CALL_SPREAD'    // Credit spread - bearish high IV only
];

const AGGRESSIVE_STRATEGIES: StrategyType[] = [
  'LONG_CALL',          // Single leg - higher risk/reward, requires delta 0.60+
  'LONG_PUT'            // Single leg - bearish conviction play
];

// Generate all applicable strategies
export function generateStrategies(
  market: MarketData,
  tech: TechnicalData,
  opt: OptionsData,
  riskFlags: RiskFlags,
  riskTolerance: 5 | 10,
  volMetrics?: VolatilityMetrics
): StrategyRecommendation[] {
  const strategies: StrategyRecommendation[] = [];

  // Determine market conditions
  const { direction, confidence, reasoning } = determineMarketDirection(tech, market);
  const ivEnv = determineIVEnvironment(opt.ivRank);

  // Get applicable strategy types from matrix
  let applicableStrategies = [...STRATEGY_MATRIX[direction][ivEnv]];

  // === REGIME-AWARE STRATEGY ADJUSTMENT ===
  // Override strategy ordering based on quantitative regime and vol edge
  if (volMetrics && volMetrics.dataSource !== 'synthetic') {
    const regime = volMetrics.regime.state;
    const edge = volMetrics.edgeSignal;

    if (regime === 'HIGH_VARIANCE' && edge === 'SELL_PREMIUM' && volMetrics.edgeStrength !== 'WEAK') {
      // High vol + overpriced options → prioritize credit strategies
      const creditFirst = applicableStrategies.filter(s =>
        ['BULL_PUT_SPREAD', 'BEAR_CALL_SPREAD'].includes(s));
      const rest = applicableStrategies.filter(s =>
        !['BULL_PUT_SPREAD', 'BEAR_CALL_SPREAD'].includes(s));
      if (creditFirst.length > 0) {
        applicableStrategies = [...creditFirst, ...rest];
      }
    } else if (regime === 'TRENDING' && edge === 'BUY_PREMIUM') {
      // Trending + cheap options → prioritize debit/directional strategies
      const debitFirst = applicableStrategies.filter(s =>
        ['BULL_CALL_SPREAD', 'BEAR_PUT_SPREAD', 'LONG_CALL', 'LONG_PUT'].includes(s));
      const rest = applicableStrategies.filter(s =>
        !['BULL_CALL_SPREAD', 'BEAR_PUT_SPREAD', 'LONG_CALL', 'LONG_PUT'].includes(s));
      if (debitFirst.length > 0) {
        applicableStrategies = [...debitFirst, ...rest];
      }
    } else if (regime === 'MEAN_REVERTING' || regime === 'STABLE') {
      // Range-bound → prefer diagonal/calendar spreads
      const timeFirst = applicableStrategies.filter(s =>
        ['DIAGONAL_SPREAD', 'CALENDAR_SPREAD'].includes(s));
      const rest = applicableStrategies.filter(s =>
        !['DIAGONAL_SPREAD', 'CALENDAR_SPREAD'].includes(s));
      if (timeFirst.length > 0) {
        applicableStrategies = [...timeFirst, ...rest];
      }
    }
    // COMPRESSING regime: keep existing order (long gamma is handled by matrix)
  }

  // RISK TOLERANCE FILTERING
  // 5% risk: Prefer conservative strategies, exclude single-leg options
  // 10% risk: Allow aggressive strategies including single-leg options
  if (riskTolerance === 5) {
    // Filter to prefer conservative strategies
    const conservativeOptions = applicableStrategies.filter(s => CONSERVATIVE_STRATEGIES.includes(s));

    // Also allow debit spreads as they have defined risk
    const debitSpreads = applicableStrategies.filter(s =>
      ['BULL_CALL_SPREAD', 'BEAR_PUT_SPREAD', 'CALENDAR_SPREAD'].includes(s)
    );

    // Combine but prioritize conservative
    applicableStrategies = [...new Set([...conservativeOptions, ...debitSpreads])];

    // If no strategies left, fall back to safest options
    if (applicableStrategies.length === 0) {
      applicableStrategies = direction === 'BULLISH'
        ? ['BULL_PUT_SPREAD', 'BULL_CALL_SPREAD']
        : direction === 'BEARISH'
          ? ['BEAR_CALL_SPREAD', 'BEAR_PUT_SPREAD']
          : ['IRON_CONDOR'];
    }
  } else {
    // 10% risk: Include aggressive strategies, prioritize them for higher R:R
    const aggressiveInMatrix = applicableStrategies.filter(s => AGGRESSIVE_STRATEGIES.includes(s));

    // Reorder to put aggressive strategies first for 10% risk seekers
    applicableStrategies = [...aggressiveInMatrix, ...applicableStrategies.filter(s => !AGGRESSIVE_STRATEGIES.includes(s))];
  }

  // Calculate common parameters
  const livePrice = market.price;
  const atr = tech.atr ?? livePrice * 0.02;
  const atrPercent = atr / livePrice;

  // Spread width based on price level AND risk tolerance
  // All widths are whole dollar amounts for Robinhood/exchange compatibility
  let spreadWidth = 5;
  if (livePrice < 50) spreadWidth = riskTolerance === 5 ? 2 : 5;
  else if (livePrice < 150) spreadWidth = riskTolerance === 5 ? 5 : 10;
  else if (livePrice < 400) spreadWidth = riskTolerance === 5 ? 10 : 15;
  else spreadWidth = riskTolerance === 5 ? 20 : 25;

  // Generate each applicable strategy
  applicableStrategies.forEach((strategyType, index) => {
    const expiry = getExpiryRecommendation(
      strategyType,
      riskFlags.earningsInDays,
      opt.ivRank,
      direction
    );

    const strategy = buildStrategy(
      strategyType,
      livePrice,
      spreadWidth,
      expiry,
      opt,
      tech,
      direction,
      ivEnv,
      confidence,
      reasoning,
      index,
      riskTolerance,
      riskFlags
    );

    if (strategy) {
      strategies.push(strategy);
    }
  });

  // Sort by suitability score
  strategies.sort((a, b) => b.suitabilityScore - a.suitabilityScore);

  return strategies;
}

// Build individual strategy
function buildStrategy(
  strategyType: StrategyType,
  livePrice: number,
  spreadWidth: number,
  expiry: ExpiryRecommendation,
  opt: OptionsData,
  tech: TechnicalData,
  direction: MarketDirection,
  ivEnv: IVEnvironment,
  confidence: number,
  directionReasoning: string,
  rank: number,
  riskTolerance: 5 | 10 = 5,
  riskFlags?: RiskFlags
): StrategyRecommendation | null {

  const baseOptionPrice = livePrice * 0.03 * (opt.ivRank / 30);
  const legs: OptionLeg[] = [];
  let maxProfit = 0;
  let maxLoss = 0;
  let breakeven = livePrice;
  let creditDebit: 'CREDIT' | 'DEBIT' = 'DEBIT';
  let riskProfile: 'DEFINED' | 'UNDEFINED' | 'SEMI_DEFINED' = 'DEFINED';

  const atmStrike = roundStrike(Math.round(livePrice / spreadWidth) * spreadWidth);

  switch (strategyType) {
    case 'LONG_CALL': {
      const premium = Math.max(0.50, baseOptionPrice);
      legs.push({
        action: 'BUY',
        amount: 1,
        type: 'CALL',
        strike: atmStrike,
        expiry: expiry.label,
        dte: expiry.dte,
        delta: 0.50,
        gamma: 0.03,
        bid: premium * 0.95,
        ask: premium * 1.05,
        openInterest: Math.round(opt.totalOpenInterest / 10)
      });
      maxLoss = Math.round(premium * 100);
      maxProfit = 999999; // Unlimited
      breakeven = atmStrike + premium;
      creditDebit = 'DEBIT';
      riskProfile = 'DEFINED';
      break;
    }

    case 'LONG_PUT': {
      const premium = Math.max(0.50, baseOptionPrice);
      legs.push({
        action: 'BUY',
        amount: 1,
        type: 'PUT',
        strike: atmStrike,
        expiry: expiry.label,
        dte: expiry.dte,
        delta: -0.50,
        gamma: 0.03,
        bid: premium * 0.95,
        ask: premium * 1.05,
        openInterest: Math.round(opt.totalOpenInterest / 10)
      });
      maxLoss = Math.round(premium * 100);
      maxProfit = Math.round((atmStrike - premium) * 100);
      breakeven = atmStrike - premium;
      creditDebit = 'DEBIT';
      riskProfile = 'DEFINED';
      break;
    }

    case 'BULL_CALL_SPREAD': {
      const longPremium = Math.max(0.60, baseOptionPrice * 1.2);
      const shortPremium = Math.max(0.20, baseOptionPrice * 0.4);
      const netDebit = longPremium - shortPremium;

      legs.push({
        action: 'BUY',
        amount: 1,
        type: 'CALL',
        strike: atmStrike,
        expiry: expiry.label,
        dte: expiry.dte,
        delta: 0.50,
        gamma: 0.03,
        bid: longPremium * 0.95,
        ask: longPremium * 1.05,
        openInterest: Math.round(opt.totalOpenInterest / 10)
      });
      legs.push({
        action: 'SELL',
        amount: 1,
        type: 'CALL',
        strike: roundStrike(atmStrike + spreadWidth),
        expiry: expiry.label,
        dte: expiry.dte,
        delta: 0.30,
        gamma: 0.02,
        bid: shortPremium * 0.95,
        ask: shortPremium * 1.05,
        openInterest: Math.round(opt.totalOpenInterest / 15)
      });

      maxLoss = Math.round(netDebit * 100);
      maxProfit = Math.round((spreadWidth - netDebit) * 100);
      breakeven = roundStrike(atmStrike + netDebit);
      creditDebit = 'DEBIT';
      break;
    }

    case 'BEAR_PUT_SPREAD': {
      const longPremium = Math.max(0.60, baseOptionPrice * 1.2);
      const shortPremium = Math.max(0.20, baseOptionPrice * 0.4);
      const netDebit = longPremium - shortPremium;

      legs.push({
        action: 'BUY',
        amount: 1,
        type: 'PUT',
        strike: atmStrike,
        expiry: expiry.label,
        dte: expiry.dte,
        delta: -0.50,
        gamma: 0.03,
        bid: longPremium * 0.95,
        ask: longPremium * 1.05,
        openInterest: Math.round(opt.totalOpenInterest / 10)
      });
      legs.push({
        action: 'SELL',
        amount: 1,
        type: 'PUT',
        strike: roundStrike(atmStrike - spreadWidth),
        expiry: expiry.label,
        dte: expiry.dte,
        delta: -0.30,
        gamma: 0.02,
        bid: shortPremium * 0.95,
        ask: shortPremium * 1.05,
        openInterest: Math.round(opt.totalOpenInterest / 15)
      });

      maxLoss = Math.round(netDebit * 100);
      maxProfit = Math.round((spreadWidth - netDebit) * 100);
      breakeven = roundStrike(atmStrike - netDebit);
      creditDebit = 'DEBIT';
      break;
    }

    case 'BULL_PUT_SPREAD': {
      // More realistic OTM credit spread pricing
      // Short put ~5-10% OTM, long put further OTM
      // Net credit typically 20-35% of spread width for reasonable probability trades
      const shortStrike = roundStrike(atmStrike - spreadWidth);
      const longStrike = roundStrike(atmStrike - (spreadWidth * 2));

      // Calculate realistic premiums based on how far OTM
      const shortOtmPercent = (livePrice - shortStrike) / livePrice;
      const longOtmPercent = (livePrice - longStrike) / livePrice;

      // OTM options have lower premiums - use IV to scale
      const ivMultiplier = Math.max(0.5, opt.ivRank / 50); // Higher IV = more premium
      const shortPremium = spreadWidth * 0.25 * ivMultiplier * Math.max(0.3, 1 - shortOtmPercent * 5);
      const longPremium = spreadWidth * 0.08 * ivMultiplier * Math.max(0.2, 1 - longOtmPercent * 5);
      const netCredit = Math.max(0.30, shortPremium - longPremium);

      legs.push({
        action: 'SELL',
        amount: 1,
        type: 'PUT',
        strike: shortStrike,
        expiry: expiry.label,
        dte: expiry.dte,
        delta: -0.25,
        gamma: 0.02,
        bid: shortPremium * 0.95,
        ask: shortPremium * 1.05,
        openInterest: Math.round(opt.totalOpenInterest / 12)
      });
      legs.push({
        action: 'BUY',
        amount: 1,
        type: 'PUT',
        strike: longStrike,
        expiry: expiry.label,
        dte: expiry.dte,
        delta: -0.10,
        gamma: 0.01,
        bid: longPremium * 0.95,
        ask: longPremium * 1.05,
        openInterest: Math.round(opt.totalOpenInterest / 20)
      });

      maxProfit = Math.round(netCredit * 100);
      maxLoss = Math.round((spreadWidth - netCredit) * 100);
      breakeven = atmStrike - spreadWidth + netCredit;
      creditDebit = 'CREDIT';
      break;
    }

    case 'BEAR_CALL_SPREAD': {
      // More realistic OTM credit spread pricing for calls
      const shortStrike = roundStrike(atmStrike + spreadWidth);
      const longStrike = roundStrike(atmStrike + (spreadWidth * 2));

      // Calculate realistic premiums based on how far OTM
      const shortOtmPercent = (shortStrike - livePrice) / livePrice;
      const longOtmPercent = (longStrike - livePrice) / livePrice;

      // OTM calls - use IV to scale
      const ivMultiplier = Math.max(0.5, opt.ivRank / 50);
      const shortPremium = spreadWidth * 0.25 * ivMultiplier * Math.max(0.3, 1 - shortOtmPercent * 5);
      const longPremium = spreadWidth * 0.08 * ivMultiplier * Math.max(0.2, 1 - longOtmPercent * 5);
      const netCredit = Math.max(0.30, shortPremium - longPremium);

      legs.push({
        action: 'SELL',
        amount: 1,
        type: 'CALL',
        strike: shortStrike,
        expiry: expiry.label,
        dte: expiry.dte,
        delta: 0.25,
        gamma: 0.02,
        bid: shortPremium * 0.95,
        ask: shortPremium * 1.05,
        openInterest: Math.round(opt.totalOpenInterest / 12)
      });
      legs.push({
        action: 'BUY',
        amount: 1,
        type: 'CALL',
        strike: longStrike,
        expiry: expiry.label,
        dte: expiry.dte,
        delta: 0.10,
        gamma: 0.01,
        bid: longPremium * 0.95,
        ask: longPremium * 1.05,
        openInterest: Math.round(opt.totalOpenInterest / 20)
      });

      maxProfit = Math.round(netCredit * 100);
      maxLoss = Math.round((spreadWidth - netCredit) * 100);
      breakeven = atmStrike + spreadWidth + netCredit;
      creditDebit = 'CREDIT';
      break;
    }

    case 'IRON_CONDOR': {
      const putShort = roundStrike(atmStrike - spreadWidth);
      const putLong = roundStrike(atmStrike - (spreadWidth * 2));
      const callShort = roundStrike(atmStrike + spreadWidth);
      const callLong = roundStrike(atmStrike + (spreadWidth * 2));
      const creditPerSide = Math.max(0.25, baseOptionPrice * 0.5);
      const totalCredit = creditPerSide * 2;

      legs.push(
        { action: 'BUY', amount: 1, type: 'PUT', strike: putLong, expiry: expiry.label, dte: expiry.dte, delta: -0.10, gamma: 0.01, bid: creditPerSide * 0.4, ask: creditPerSide * 0.5, openInterest: 1000 },
        { action: 'SELL', amount: 1, type: 'PUT', strike: putShort, expiry: expiry.label, dte: expiry.dte, delta: -0.25, gamma: 0.02, bid: creditPerSide * 0.9, ask: creditPerSide * 1.1, openInterest: 1500 },
        { action: 'SELL', amount: 1, type: 'CALL', strike: callShort, expiry: expiry.label, dte: expiry.dte, delta: 0.25, gamma: 0.02, bid: creditPerSide * 0.9, ask: creditPerSide * 1.1, openInterest: 1500 },
        { action: 'BUY', amount: 1, type: 'CALL', strike: callLong, expiry: expiry.label, dte: expiry.dte, delta: 0.10, gamma: 0.01, bid: creditPerSide * 0.4, ask: creditPerSide * 0.5, openInterest: 1000 }
      );

      maxProfit = Math.round(totalCredit * 100);
      maxLoss = Math.round((spreadWidth - totalCredit) * 100);
      breakeven = putShort; // Simplified - actual has two breakevens
      creditDebit = 'CREDIT';
      break;
    }

    case 'STRADDLE': {
      const premium = Math.max(0.80, baseOptionPrice * 1.5);

      legs.push(
        { action: 'BUY', amount: 1, type: 'CALL', strike: atmStrike, expiry: expiry.label, dte: expiry.dte, delta: 0.50, gamma: 0.04, bid: premium * 0.95, ask: premium * 1.05, openInterest: 2000 },
        { action: 'BUY', amount: 1, type: 'PUT', strike: atmStrike, expiry: expiry.label, dte: expiry.dte, delta: -0.50, gamma: 0.04, bid: premium * 0.95, ask: premium * 1.05, openInterest: 2000 }
      );

      const totalPremium = premium * 2;
      maxLoss = Math.round(totalPremium * 100);
      maxProfit = 999999; // Unlimited on upside
      breakeven = atmStrike; // Simplified
      creditDebit = 'DEBIT';
      riskProfile = 'DEFINED';
      break;
    }

    case 'STRANGLE': {
      const callStrike = roundStrike(atmStrike + spreadWidth);
      const putStrike = roundStrike(atmStrike - spreadWidth);
      const premium = Math.max(0.40, baseOptionPrice * 0.8);

      legs.push(
        { action: 'BUY', amount: 1, type: 'CALL', strike: callStrike, expiry: expiry.label, dte: expiry.dte, delta: 0.30, gamma: 0.03, bid: premium * 0.95, ask: premium * 1.05, openInterest: 1500 },
        { action: 'BUY', amount: 1, type: 'PUT', strike: putStrike, expiry: expiry.label, dte: expiry.dte, delta: -0.30, gamma: 0.03, bid: premium * 0.95, ask: premium * 1.05, openInterest: 1500 }
      );

      const totalPremium = premium * 2;
      maxLoss = Math.round(totalPremium * 100);
      maxProfit = 999999;
      breakeven = atmStrike;
      creditDebit = 'DEBIT';
      riskProfile = 'DEFINED';
      break;
    }

    default:
      return null;
  }

  // Calculate probability of profit based on strategy type and conditions
  let pop = 0.50;
  if (creditDebit === 'CREDIT') {
    pop = 0.60 + (opt.ivRank / 500); // Credit spreads have higher POP
  } else {
    pop = 0.40 + (confidence / 300); // Debit spreads depend on direction confidence
  }

  // Suitability score based on rank, conditions, AND risk tolerance match
  let suitabilityBonus = 0;

  // Boost score if strategy matches risk tolerance
  const isConservativeStrategy = CONSERVATIVE_STRATEGIES.includes(strategyType);
  const isAggressiveStrategy = AGGRESSIVE_STRATEGIES.includes(strategyType);

  if (riskTolerance === 5 && isConservativeStrategy) {
    suitabilityBonus = 10; // Conservative user gets conservative strategy
  } else if (riskTolerance === 10 && isAggressiveStrategy) {
    suitabilityBonus = 10; // Aggressive user gets aggressive strategy
  } else if (riskTolerance === 5 && isAggressiveStrategy) {
    suitabilityBonus = -15; // Penalize aggressive strategies for conservative users
  }

  // Build warnings FIRST so penalty code can push into it
  const warnings: string[] = [];
  if (opt.ivRank > 70) warnings.push('High IV - expect potential IV crush');
  if (opt.liquidityGrade !== 'A') warnings.push('Check liquidity before entering');
  if (expiry.dte < 14) warnings.push('Short-dated option - monitor closely');
  if (riskTolerance === 5 && isAggressiveStrategy) warnings.push('Higher risk strategy - size position conservatively');
  if (riskTolerance === 10 && isConservativeStrategy) warnings.push('Conservative strategy - potential may be limited');

  // --- EARNINGS PROXIMITY PENALTY ---
  // Credit spreads into imminent earnings are extremely dangerous
  const earningsDays = riskFlags?.earningsInDays ?? null;
  if (creditDebit === 'CREDIT' && earningsDays !== null && earningsDays <= 7) {
    suitabilityBonus -= 35; // Major penalty — binary gap risk far exceeds credit collected
    warnings.push(`CRITICAL: Credit spread near earnings (${earningsDays}d) — gap risk far exceeds premium collected`);
  } else if (creditDebit === 'CREDIT' && earningsDays !== null && earningsDays <= 14) {
    suitabilityBonus -= 15; // Moderate penalty
    warnings.push(`Earnings in ${earningsDays}d — elevated risk for credit spreads`);
  }

  // --- RISK/REWARD PENALTY ---
  // Penalize strategies where you risk much more than you can make
  const rrRatio = maxProfit / Math.max(1, maxLoss);
  if (rrRatio < 0.25) {
    suitabilityBonus -= 20; // 4:1+ risk:reward is terrible
    warnings.push(`Poor risk/reward: risking $${maxLoss} to make $${maxProfit} (${(1/rrRatio).toFixed(1)}:1 against you)`);
  } else if (rrRatio < 0.5) {
    suitabilityBonus -= 10; // 2:1+ risk:reward is unfavorable
    warnings.push(`Unfavorable risk/reward: ${(1/rrRatio).toFixed(1)}:1 risk to reward`);
  }

  // --- CUSHION VS EXPECTED MOVE ---
  // For credit spreads, check if short strike has enough room vs implied move
  if (creditDebit === 'CREDIT' && earningsDays !== null && earningsDays <= 7) {
    const dailyIV = (opt.ivRank / 100) * 0.5;
    const expectedMovePercent = Math.max(3, dailyIV * Math.sqrt(earningsDays) * 100);
    const breakevenVal = Array.isArray(breakeven) ? breakeven[0] : breakeven;
    const cushionPercent = Math.abs(livePrice - breakevenVal) / livePrice * 100;

    if (cushionPercent < expectedMovePercent) {
      suitabilityBonus -= 15;
      warnings.push(`Cushion (${cushionPercent.toFixed(1)}%) less than expected earnings move (~${expectedMovePercent.toFixed(0)}%)`);
    }
  }

  const suitabilityScore = Math.max(5, Math.min(95, 85 - (rank * 10) + (confidence / 10) + suitabilityBonus));

  // Build suitability reasons with risk tolerance context
  const riskContext = riskTolerance === 5
    ? 'Conservative 5% risk profile'
    : 'Growth-focused 10% risk profile';

  const reasons = [
    `${direction} market bias (${confidence}% confidence)`,
    `${ivEnv} IV environment (IV Rank: ${opt.ivRank})`,
    `${riskContext} - ${riskProfile === 'DEFINED' ? 'defined risk trade' : 'higher R:R potential'}`,
    expiry.reasoning.split('.')[0]
  ];

  return {
    id: `strat-${strategyType.toLowerCase()}-${Date.now()}`,
    name: formatStrategyName(strategyType),
    type: getStrategyCategory(strategyType),
    direction: direction,
    suitabilityScore,
    suitabilityReasons: reasons,
    riskProfile,
    creditDebit,
    legs,
    maxProfit,
    maxLoss,
    breakeven,
    probabilityOfProfit: Number(pop.toFixed(2)),
    riskRewardRatio: maxLoss > 0 ? Number((maxProfit / maxLoss).toFixed(2)) : 999,
    greeks: {
      delta: legs.reduce((sum, leg) => sum + (leg.action === 'BUY' ? leg.delta : -leg.delta), 0),
      theta: creditDebit === 'CREDIT' ? 0.02 : -0.03,
      gamma: 0.02,
      vega: creditDebit === 'CREDIT' ? -0.01 : 0.01,
      deltaIdeal: direction === 'BULLISH' ? 'POSITIVE' : direction === 'BEARISH' ? 'NEGATIVE' : 'NEUTRAL',
      vegaIdeal: ivEnv === 'LOW' ? 'POSITIVE' : 'NEGATIVE',
      thetaIdeal: creditDebit === 'CREDIT' ? 'POSITIVE' : 'NEUTRAL',
      gammaIdeal: 'LOW',
      thetaDailyDollar: creditDebit === 'CREDIT' ? 2 : -3
    },
    warnings,
    expiryReasoning: expiry.reasoning,
    strategyExplanation: getStrategyExplanation(strategyType, direction, ivEnv),
    directionReasoning
  };
}

function formatStrategyName(type: StrategyType): string {
  const names: Record<StrategyType, string> = {
    'LONG_CALL': 'Long Call',
    'LONG_PUT': 'Long Put',
    'BULL_CALL_SPREAD': 'Bull Call Spread',
    'BEAR_PUT_SPREAD': 'Bear Put Spread',
    'BULL_PUT_SPREAD': 'Bull Put Spread (Credit)',
    'BEAR_CALL_SPREAD': 'Bear Call Spread (Credit)',
    'IRON_CONDOR': 'Iron Condor',
    'IRON_BUTTERFLY': 'Iron Butterfly',
    'STRADDLE': 'Long Straddle',
    'STRANGLE': 'Long Strangle',
    'CALENDAR_SPREAD': 'Calendar Spread',
    'DIAGONAL_SPREAD': 'Diagonal Spread',
    'COVERED_CALL': 'Covered Call',
    'CASH_SECURED_PUT': 'Cash-Secured Put'
  };
  return names[type] || type;
}

function getStrategyCategory(type: StrategyType): 'VERTICAL' | 'SINGLE' | 'IRON' | 'STRADDLE' | 'CALENDAR' {
  if (['LONG_CALL', 'LONG_PUT', 'COVERED_CALL', 'CASH_SECURED_PUT'].includes(type)) return 'SINGLE';
  if (['IRON_CONDOR', 'IRON_BUTTERFLY'].includes(type)) return 'IRON';
  if (['STRADDLE', 'STRANGLE'].includes(type)) return 'STRADDLE';
  if (['CALENDAR_SPREAD', 'DIAGONAL_SPREAD'].includes(type)) return 'CALENDAR';
  return 'VERTICAL';
}

// ============================================
// v4.0: TRAILING STOPS & TIERED EXITS
// ============================================

/**
 * Calculate the trailing stop price based on current profit level
 * v4.0 Enhancement: Dynamic ATR-based trailing that tightens as profit increases
 */
export function calculateTrailingStop(
  entryPrice: number,
  currentPrice: number,
  atr: number,
  direction: 'BULLISH' | 'BEARISH' | 'NEUTRAL',
  maxProfit: number
): TrailingStopConfig {
  // Calculate current profit percentage
  const currentProfit = direction === 'BEARISH'
    ? entryPrice - currentPrice
    : currentPrice - entryPrice;
  const profitPercent = maxProfit > 0 ? (currentProfit / maxProfit) * 100 : 0;

  // Define trailing levels - tighten as profit increases
  const profitLevels: TrailingStopConfig['profitLevels'] = [
    { profitPercent: 0, atrMultiple: 1.5 },    // Initial: 1.5x ATR
    { profitPercent: 25, atrMultiple: 1.25 },  // At 25% profit: 1.25x ATR
    { profitPercent: 50, atrMultiple: 1.0 },   // At 50% profit: 1x ATR
    { profitPercent: 100, atrMultiple: 0.75 }, // At 100% profit: 0.75x ATR
    { profitPercent: 150, atrMultiple: 0.5 }   // At 150%+ profit: 0.5x ATR (tight trail)
  ];

  // Find the appropriate ATR multiple for current profit level
  let atrMultiple = 1.5; // Default
  for (const level of profitLevels) {
    if (profitPercent >= level.profitPercent) {
      atrMultiple = level.atrMultiple;
    }
  }

  // Calculate the trailing stop price
  let currentStopPrice: number;
  if (direction === 'BEARISH') {
    currentStopPrice = currentPrice + (atr * atrMultiple);
  } else {
    currentStopPrice = currentPrice - (atr * atrMultiple);
  }

  return {
    enabled: true,
    initialMultiple: 1.5,
    profitLevels,
    currentLevel: profitPercent,
    currentStopPrice: Math.round(currentStopPrice * 100) / 100,
    atrValue: atr
  };
}

/**
 * Generate tiered exit configuration
 * v4.0 Enhancement: 30/30/trail vs flat 50% exit
 */
export function getTieredExitConfig(creditDebit: 'CREDIT' | 'DEBIT'): TieredExitConfig {
  if (creditDebit === 'CREDIT') {
    // Credit spreads: still use 50% profit target but can hold longer
    return {
      tier1: { percent: 50, exitPercent: 50 },   // Trim 50% at 50% profit
      tier2: { percent: 75, exitPercent: 50 },   // Trim remaining at 75%
      runner: { trailWithATR: false, atrMultiple: 0 } // No runner for credit
    };
  }

  // Debit spreads: 30/30/trail approach
  return {
    tier1: { percent: 50, exitPercent: 30 },   // Trim 30% at 50% profit
    tier2: { percent: 100, exitPercent: 30 },  // Trim 30% at 100% profit
    runner: { trailWithATR: true, atrMultiple: 0.75 } // Trail 40% with tight stops
  };
}

/**
 * Check if position should be held based on momentum
 * v4.0 Enhancement: Override exits if momentum is strongly aligned
 */
export function checkMomentumOverride(
  tech: TechnicalData,
  market: MarketData,
  direction: 'BULLISH' | 'BEARISH' | 'NEUTRAL'
): MomentumOverride {
  const reasons: string[] = [];
  let rsiAligned = false;
  let macdAligned = false;
  let volumeConfirmed = false;

  // Check RSI alignment
  if (direction === 'BULLISH' && tech.rsi > 55 && tech.rsi < 75) {
    rsiAligned = true;
    reasons.push(`RSI ${tech.rsi.toFixed(0)} shows bullish momentum without overbought`);
  } else if (direction === 'BEARISH' && tech.rsi < 45 && tech.rsi > 25) {
    rsiAligned = true;
    reasons.push(`RSI ${tech.rsi.toFixed(0)} shows bearish momentum without oversold`);
  }

  // Check MACD alignment
  if (direction === 'BULLISH' && tech.macd.crossover === 'BULLISH') {
    macdAligned = true;
    reasons.push('MACD bullish crossover intact');
  } else if (direction === 'BEARISH' && tech.macd.crossover === 'BEARISH') {
    macdAligned = true;
    reasons.push('MACD bearish crossover intact');
  }

  // Check volume confirmation
  const volumeRatio = tech.volumeRatio ?? (market.volume / market.avgVolume);
  if (volumeRatio >= 1.2) {
    volumeConfirmed = true;
    reasons.push(`Volume ${(volumeRatio * 100).toFixed(0)}% of average confirms move`);
  }

  // Hold if at least 2 of 3 conditions are met
  const alignedCount = [rsiAligned, macdAligned, volumeConfirmed].filter(Boolean).length;
  const shouldHold = alignedCount >= 2;

  if (shouldHold) {
    reasons.unshift('MOMENTUM OVERRIDE: Hold position');
  }

  return {
    shouldHold,
    reasons,
    rsiAligned,
    macdAligned,
    volumeConfirmed
  };
}

/**
 * Generate exit checklist for trade plan
 * v4.0 Enhancement: More detailed, tiered exit instructions
 */
export function generateExitChecklist(
  tieredConfig: TieredExitConfig,
  maxProfit: number,
  creditDebit: 'CREDIT' | 'DEBIT',
  earningsDays: number | null
): string[] {
  const checklist: string[] = [];

  if (creditDebit === 'CREDIT') {
    checklist.push(`✅ TIER 1: Close ${tieredConfig.tier1.exitPercent}% at ${tieredConfig.tier1.percent}% profit ($${Math.round(maxProfit * tieredConfig.tier1.percent / 100)})`);
    checklist.push(`🎯 TIER 2: Close remaining at ${tieredConfig.tier2.percent}% profit or 21 DTE`);
    checklist.push(`🛑 STOP: Exit all if loss reaches 1x credit received`);
  } else {
    const tier1Profit = Math.round(maxProfit * tieredConfig.tier1.percent / 100);
    const tier2Profit = Math.round(maxProfit * tieredConfig.tier2.percent / 100);

    checklist.push(`✅ TIER 1: Trim ${tieredConfig.tier1.exitPercent}% at ${tieredConfig.tier1.percent}% profit ($${tier1Profit})`);
    checklist.push(`✅ TIER 2: Trim ${tieredConfig.tier2.exitPercent}% at ${tieredConfig.tier2.percent}% profit ($${tier2Profit})`);
    checklist.push(`🎯 RUNNER: Trail remaining ${100 - tieredConfig.tier1.exitPercent - tieredConfig.tier2.exitPercent}% with ${tieredConfig.runner.atrMultiple}x ATR stop`);
    checklist.push(`🛑 STOP: Written at entry — move to breakeven after Tier 1`);
  }

  // Earnings rule
  if (earningsDays !== null && earningsDays <= 56) {
    checklist.push(`⚠️ EARNINGS: Close ALL positions 2 days before earnings (day ${earningsDays})`);
  } else {
    checklist.push(`✅ No imminent earnings — standard DTE rules apply`);
  }

  // DTE clock
  checklist.push(`📅 DTE CLOCK: 21 DTE → reassess | 14 DTE → close if losing | 7 DTE → close 100%`);

  return checklist;
}
